import {
  useAttachmentReadSignedUrlLazyQuery,
  useAttachmentsQuery,
  useDeleteCandidateAttachmentMutation,
} from "@/shared/services/api/graphql/generated";
import { useToast } from "@/shared/contexts/toast.context";
import { toNumber } from "lodash";

interface AttachmentParams {
  user_id?: number;
  person_id?: number;
  position_id?: number;
}

export interface AttachmentMetadata {
  file_name: string;
  content_type: string;
  description?: string;
  tags?: string[];
}

export const useAttachmentRead = (params: AttachmentParams) => {
  const [getAttachmentRead, { loading }] = useAttachmentReadSignedUrlLazyQuery({
    nextFetchPolicy: "network-only",
  });

  const readAttachment = async (id: number) => {
    const { data } = await getAttachmentRead({
      variables: {
        ...params,
        id,
      },
    });

    // Download the file
    if (data?.attachmentReadSignedUrl) {
      const response = await fetch(data?.attachmentReadSignedUrl || "");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }

    return data?.attachmentReadSignedUrl;
  };

  return {
    readAttachment,
    loading,
  };
};

export const useAttachments = (params: AttachmentParams, extraOptions?: {}) => {
  const { data, loading } = useAttachmentsQuery({
    variables: {
      attachmentsFilter: {
        position_id: params.position_id,
        person_id: params.person_id || null,
      },
    },
    ...extraOptions,
  });

  return {
    attachments: data?.attachments,
    loading,
  };
};

export const useAttachmentDelete = (params: AttachmentParams) => {
  const [deleteAttachment, { loading }] =
    useDeleteCandidateAttachmentMutation();

  const { showToast } = useToast();

  const handleDeleteAttachment = async (id: number) => {
    const { data } = await deleteAttachment({
      variables: {
        ...params,
        id,
      },
      refetchQueries: ["CandidateAddonData", "Attachments"],
    });

    if (data?.deleteCandidateAttachment) {
      showToast({
        message: "Success!",
        description: "Attachment deleted successfully",
        type: "success",
      });
    }

    return data?.deleteCandidateAttachment;
  };

  return {
    handleDeleteAttachment,
    loading,
  };
};
